import React, { useState } from 'react';
import { translate } from '@airasia-common/libraries/i18n/LocaleProvider';
import styled from 'styled-components';
import theme from '@airasia-common/libraries/theming/theme';
import { object, arrayOf, string, instanceOf, bool } from 'prop-types';
import {
  formatShortLocalTime,
  formatLongLocalTime,
  TIMELINE_DESTINATION_TIME_FORMAT,
} from '@airasia-common/libraries/datetime';
import {
  colorMap,
  getTimelineDescription,
} from '@airasia-common/libraries/utilities/TimelineHelper';
import { CANCELLATION_PENALTY_TYPES } from '@airasia-common/libraries/constants';
import { DateTime } from 'luxon';
import Wrapper from '../Wrapper';
import Row from '../Container/Row';
import Text from '../Text/Text';
import ShowMore from '../ShowMore';
import { SubBodyText, CaptionText } from '../Text/Heading';

const VerticalDivider = styled(Wrapper)`
  height: 79px;
  width: 4px;
`;

const CheckPointLabel = styled(Text)`
  font-size: 16px;
  font-weight: normal;
  line-height: 20px;
`;
const DestinationTimeLabel = styled(Text)`
  padding-left: 8px;
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.greyDark};
`;

const CheckPointIcon = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.colors.greyDarker};
  margin-right: 9px;
`;

const TimelineCheckpoint = ({ checkpoint, destinationTime }) => {
  return (
    <Row alignItems="center" backgroundColor="transparent" marginTop="-4px">
      <CheckPointIcon marginRight={9} />
      <CheckPointLabel>{checkpoint}</CheckPointLabel>
      {destinationTime && (
        <DestinationTimeLabel>{destinationTime}</DestinationTimeLabel>
      )}
    </Row>
  );
};

TimelineCheckpoint.propTypes = {
  checkpoint: string,
  destinationTime: string,
};

const TimelineContent = ({ item, firstTimeline, isPayAtHotel }) => {
  const timelineDescription = getTimelineDescription(item);

  const translateCancellationPolicy = key => {
    if (isPayAtHotel && CANCELLATION_PENALTY_TYPES.NONE === key) {
      return translate(`cancellationPolicy.payAtHotel.${key}`);
    } else if (isPayAtHotel && CANCELLATION_PENALTY_TYPES.FULL === key) {
      return translate(`cancellationPolicy.description.NoRefund`);
    }
    return translate(`cancellationPolicy.${key}`);
  };

  return (
    <Row alignItems="center" backgroundColor="transparent" marginTop="-4px">
      <VerticalDivider
        background={colorMap(item.penalty_type)}
        marginLeft={2}
        marginRight="13px"
      />
      <Wrapper>
        <SubBodyText color={colorMap(item.penalty_type)}>
          {translateCancellationPolicy(item.penalty_type)}
        </SubBodyText>
        {timelineDescription && (
          <CaptionText
            opacity={0.8}
            lineHeight="18px"
            color={
              firstTimeline &&
              item.penalty_type === CANCELLATION_PENALTY_TYPES.PARTIAL
                ? colorMap(item.penalty_type)
                : theme.colors.greyDark
            }
          >
            {timelineDescription}
          </CaptionText>
        )}
      </Wrapper>
    </Row>
  );
};

TimelineContent.propTypes = {
  item: object,
  firstTimeline: bool,
  isPayAtHotel: bool,
};
TimelineContent.defaultProps = {
  firstTimeline: false,
  isPayAtHotel: false,
};

const Timeline = ({
  destinationInfo,
  cancelPenalties = [],
  checkin,
  isPayAtHotel,
  isFreeCancellation,
  move,
}) => {
  let [firstFrame, secondFrame] = cancelPenalties;

  const frames = cancelPenalties;

  const [shouldUpdateLayout, setShouldUpdateLayout] = useState(true);

  const cancellationZero = cancelPenalties.length === 0 && isFreeCancellation;

  const cancellationZeroPolicy =
    cancelPenalties.length === 0 && !isFreeCancellation;

  const destinationTime = date => {
    if (!destinationInfo) {
      return '';
    }
    const { city } = destinationInfo.address;

    const destinationTime = DateTime.fromISO(date, { setZone: true });
    return `(${translate('cancellationPolicy.destinationTime', {
      time: destinationTime
        .toFormat(TIMELINE_DESTINATION_TIME_FORMAT)
        .toLowerCase(),
      city: city,
    })})`;
  };

  return (
    <Wrapper>
      <Text
        color="darkBlack"
        mb="18px"
        fontSize={['16px', '24px']}
        lineHeight={['24px', '36px']}
      >
        {translate('cancellationPolicy.title')}
      </Text>

      {cancellationZero && (
        <>
          <TimelineCheckpoint
            checkpoint={translate('cancellationPolicy.now')}
          />
          <TimelineContent
            item={{ penalty_type: CANCELLATION_PENALTY_TYPES.NONE }}
            isPayAtHotel={isPayAtHotel}
          />
          <TimelineCheckpoint
            checkpoint={`${formatShortLocalTime(checkin)} (${translate(
              'cancellationPolicy.checkinDate'
            )})`}
          />
        </>
      )}
      {cancellationZeroPolicy && (
        <>
          <TimelineCheckpoint
            checkpoint={translate('cancellationPolicy.now')}
          />
          <TimelineContent
            item={{ penalty_type: CANCELLATION_PENALTY_TYPES.FULL }}
            isPayAtHotel={isPayAtHotel}
          />
          <TimelineCheckpoint
            checkpoint={`${formatShortLocalTime(checkin)} (${translate(
              'cancellationPolicy.checkinDate'
            )})`}
          />
        </>
      )}

      {cancelPenalties.length === 1 && (
        <>
          <TimelineCheckpoint
            checkpoint={translate('cancellationPolicy.now')}
          />
          <TimelineContent
            item={firstFrame}
            firstTimeline
            isPayAtHotel={isPayAtHotel}
          />
          <TimelineCheckpoint
            checkpoint={`${formatShortLocalTime(checkin)} (${translate(
              'cancellationPolicy.checkinDate'
            )})`}
          />
        </>
      )}

      {cancelPenalties.length > 1 && (
        <ShowMore
          visibleContent={
            <Wrapper>
              {!!firstFrame && (
                <>
                  <TimelineCheckpoint
                    checkpoint={translate('cancellationPolicy.now')}
                  />
                  <TimelineContent
                    item={firstFrame}
                    firstTimeline
                    isPayAtHotel={isPayAtHotel}
                  />
                </>
              )}
              {secondFrame.start && shouldUpdateLayout && (
                <TimelineCheckpoint
                  checkpoint={formatLongLocalTime(secondFrame.start)}
                  destinationTime={destinationTime(secondFrame.start)}
                />
              )}
            </Wrapper>
          }
          collapseContent={
            <Wrapper>
              {frames.map((item, index) => {
                if (item === firstFrame) {
                  return null;
                } else {
                  return (
                    <Wrapper key={index}>
                      <TimelineCheckpoint
                        checkpoint={formatLongLocalTime(item.start)}
                        destinationTime={destinationTime(item.start)}
                      />
                      <TimelineContent item={item} />
                    </Wrapper>
                  );
                }
              })}

              <TimelineCheckpoint
                checkpoint={`${formatShortLocalTime(checkin)} (${translate(
                  'cancellationPolicy.checkinDate'
                )})`}
              />
            </Wrapper>
          }
          onToggle={isCollapsed => {
            setShouldUpdateLayout(isCollapsed);
          }}
          move={move}
        />
      )}
    </Wrapper>
  );
};

Timeline.propTypes = {
  destinationInfo: object,
  cancelPenalties: arrayOf(object),
  checkin: instanceOf(Date),
  isPayAtHotel: bool,
  isFreeCancellation: bool,
  move: bool,
};

export default Timeline;
